import Konva from 'konva'
import { IRect } from 'konva/lib/types'
import { Box } from 'konva/lib/shapes/Transformer'
import EventEmitter from 'eventemitter3'
import {
  SegmentText,
  SegmentSticker,
  SegmentVideo,
  SegmentCollection,
  Track,
} from '@ve-pro/types/track.type'

import { initAnchorsStyle } from '@com/konva-modules/transformer-style'
import { ProjectSegmentModel } from '@ve-pro/api/project/type'
import {
  Snapping,
  SNAP_NODE,
  GUIDELINE_OFFSET,
  GUID_LINE,
} from '@com/konva-modules/snapping'
import { TransformerSelection } from './TransformerSelection'
import { isSegmentGroupChild } from '@ve-pro/store/tracks/segment/segment-group'
import { getUniqueId } from './utils'
import {
  getRectFromNodes,
  getNodeByPoint,
  transformRectToScale,
} from './transformer/utils'

export const MIN_RECT_WIDTH = 0
export const MIN_RECT_HEIGHT = 0
// const MAX_SCALE = 5
export const GHOST = 'ghost'

// 默认样式
const normalTransformerStyle: Konva.TransformerConfig = {
  borderStroke: '#aaa',
  anchorStroke: '#aaa',
  anchorFill: '#fff',
}

// 主题选中样式
const primaryTransformerStyle: Konva.TransformerConfig = {
  borderStroke: '#FAC415',
  anchorStroke: '#aaa',
  anchorFill: '#fff',
}

const primaryFill = 'rgba(250, 196, 21, 0.12)'

// 文本的可操作anchors
export const textEnabledAnchors = [
  'top-left',
  'top-right',
  'middle-right',
  'middle-left',
  'bottom-left',
  'bottom-right',
]

export const enabledAnchors = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
]

/**
 * 节点选中框默认样式
 */
const transformerConfig: Konva.TransformerConfig = {
  enabledAnchors: [...enabledAnchors],
  borderStrokeWidth: 1,
  anchorSize: 30,
  anchorStrokeWidth: 1,
  centeredScaling: true,
  keepRatio: true,
  // rotationSnapTolerance: 10,
  rotateAnchorOffset: 20,
  rotationSnaps: [0, 45, 90, 135, 180, 225, 270, 315],

  // boundBoxFunc: (oldBox, newBox) => {
  //   if (newBox.width < MIN_RECT_WIDTH || newBox.height < MIN_RECT_HEIGHT) {
  //     return oldBox
  //   }

  //   return newBox
  // },
}

interface TransformOptions {
  id: number
  rotation: number
  scaleX: number
  scaleY: number
  x: number
  y: number
  width: number
  height: number
}

interface Event {
  /** 节点被选中 */
  click: (event: MouseEvent, id?: ProjectSegmentModel['id']) => void
  /** 拖拽、缩放、旋转事件 */
  transform: (data: TransformOptions) => void
  transformStart: (event: MouseEvent) => void
  transformEnd: () => void
}

export function transformNodeData(node: Konva.Rect): TransformOptions {
  const rotation = Math.round(node.rotation())

  return {
    id: node.getAttr('_id'),
    rotation: rotation >= 0 ? rotation : 360 + rotation,
    scaleX: Number(node.scaleX().toFixed(2)),
    scaleY: Number(node.scaleY().toFixed(2)),
    width: node.width(),
    height: node.height(),
    x: Math.floor(node.x()),
    y: -Math.floor(node.y()), // 这里跟产品预期有些差异, 加个-
  }
}

// _layer的offset是在中心，并受缩放影响，一般用于放置普通元件
// _layer2用于放置参考线、框选元件等，offset在左上角，不受缩放影响
export class Transformer extends EventEmitter<Event> {
  private _stage!: Konva.Stage
  private _layer: Konva.Layer = new Konva.Layer()
  // layer2用来存放参考线，不用_layer是因为它会被设置缩放值
  private _layer2: Konva.Layer = new Konva.Layer()
  private _tr = new Konva.Transformer({
    ...transformerConfig,
    ...primaryTransformerStyle,
  })
  snapping!: Snapping
  selection!: TransformerSelection

  // 用于覆盖在nodes之上，实现整体的拖拽
  private _nodesCover: Konva.Rect

  // 画布的ghost元件，用来计算参考线
  private _ghostCanvas: Konva.Rect

  private _snapRect: IRect | null = null

  constructor() {
    super()

    this._ghostCanvas = this._addGhostCanvas()

    this._nodesCover = new Konva.Rect({
      draggable: true,
      // stroke: 'blue',
    })

    this._bindTransformerEvents()
  }

  init(container: HTMLDivElement, ratio = 1): void {
    const width = container.parentElement?.offsetWidth ?? 0
    const height = container.parentElement?.offsetHeight ?? 0

    this._stage = new Konva.Stage({
      container,
      width,
      height,
    })

    this._layer.setAttrs({
      scaleX: ratio,
      scaleY: ratio,
      offsetX: -width / ratio / 2,
      offsetY: -height / ratio / 2,
    })

    this._stage.add(this._layer)
    this._stage.add(this._layer2)

    // this._layer.add(this._ghostCanvas)
    this._layer2.add(this._ghostCanvas)

    this._layer.add(this._nodesCover)

    this.initTransformer()

    this.snapping = new Snapping(this._stage, this._layer2, {
      enableOnResize: true,
    })
    this.selection = new TransformerSelection(this._stage, this._layer2)
  }

  private _updateBoundBoxFunc(node: Konva.Rect): void {
    const closeLeft = (pos: Konva.Vector2d, rect: Box) => {
      if (rect.rotation === Math.PI) {
        return Math.abs(pos.x - (rect.x - rect.width)) <= GUIDELINE_OFFSET
      }
      return Math.abs(pos.x - rect.x) <= GUIDELINE_OFFSET
    }
    const closeRight = (pos: Konva.Vector2d, rect: Box) => {
      if (rect.rotation === Math.PI) {
        return Math.abs(pos.x - rect.x) <= GUIDELINE_OFFSET
      }
      return Math.abs(pos.x - (rect.x + rect.width)) <= GUIDELINE_OFFSET
    }
    const closeTop = (pos: Konva.Vector2d, rect: Box) => {
      if (rect.rotation === Math.PI) {
        return Math.abs(pos.y - (rect.y - rect.height)) <= GUIDELINE_OFFSET
      }
      return Math.abs(pos.y - rect.y) <= GUIDELINE_OFFSET
    }
    const closeBottom = (pos: Konva.Vector2d, rect: Box) => {
      if (rect.rotation === Math.PI) {
        return Math.abs(pos.y - rect.y) <= GUIDELINE_OFFSET
      }
      return Math.abs(pos.y - (rect.y + rect.height)) <= GUIDELINE_OFFSET
    }

    this._tr.boundBoxFunc((oldBox, newBox) => {
      this.snapping.enableOnResize =
        oldBox.rotation === 0 || oldBox.rotation === Math.PI

      const lines = this._layer2.find(`.${GUID_LINE}`)

      const closeLine = lines.find((line) => {
        const pos = line.getAbsolutePosition(this._layer2)

        return (
          closeLeft(pos, newBox) ||
          closeRight(pos, newBox) ||
          closeTop(pos, newBox) ||
          closeBottom(pos, newBox)
        )
      })

      if (closeLine) {
        if (!this._snapRect) {
          this._snapRect = getRectFromNodes(this._tr.nodes() as Konva.Rect[], {
            relativeTo: this._layer2,
          })
        }

        const pos = closeLine.getAbsolutePosition(this._layer2)

        if (closeRight(pos, newBox) || closeLeft(pos, newBox)) {
          return {
            ...oldBox,
            ...transformRectToScale(
              oldBox,
              oldBox.rotation === Math.PI
                ? (oldBox.width + (oldBox.x - oldBox.width - pos.x) * 2) /
                    oldBox.width
                : (oldBox.width + (oldBox.x - pos.x) * 2) / oldBox.width,
              oldBox.rotation,
            ),
          }
        } else if (closeTop(pos, newBox) || closeBottom(pos, newBox)) {
          return {
            ...oldBox,
            ...transformRectToScale(
              oldBox,
              oldBox.rotation === Math.PI
                ? (oldBox.height + (oldBox.y - oldBox.height - pos.y) * 2) /
                    oldBox.height
                : (oldBox.height + (oldBox.y - pos.y) * 2) / oldBox.height,
              oldBox.rotation,
            ),
          }
        }
      }

      this._snapRect = null

      if (newBox.width < MIN_RECT_WIDTH || newBox.height < MIN_RECT_HEIGHT) {
        return oldBox
      }

      return newBox
    })
  }

  updateLayout(width: number, height: number): void {
    this._stage.setAttrs({
      width,
      height,
    })

    this._layer.setAttrs({
      offsetX: -width / this._layer.scaleX() / 2,
      offsetY: -height / this._layer.scaleY() / 2,
    })
  }

  updateRatio(ratio: number): void {
    this._layer.setAttrs({
      scaleX: ratio,
      scaleY: ratio,
      offsetX: -this._stage.width() / ratio / 2,
      offsetY: -this._stage.height() / ratio / 2,
    })
  }

  updateGhostCanvasSize(width: number, height: number): void {
    this._ghostCanvas.setAttrs({
      width,
      height,
      // offsetX: width / 2,
      // offsetY: height / 2,
      offsetX: -this._stage.width() / 2 + width / 2,
      offsetY: -this._stage.height() / 2 + height / 2,
    })
  }

  private initTransformer(): void {
    this._layer.add(this._tr)

    this._stage.on('click', (e) => {
      // konva在拖拽时也会触发click事件，通过阻止事件冒泡也不行，这里过滤掉
      if (this.selection.isDragging) {
        return
      }

      // this.unfocus()

      const { x, y } = this._stage.getRelativePointerPosition()

      const target = getNodeByPoint(
        { x, y },
        this._layer.find((node: Konva.Rect) => {
          return node.hasName(GHOST) && node.visible()
        }),
      )

      if (!target) {
        this.emit('click', e.evt)
        return
      }

      this._focus([target] as Konva.Rect[])

      this.emit('click', e.evt, Number(target.getAttr('_id')))

      // if (e.target === this._stage || !e.target.hasName(GHOST)) {
      //   this.emit('click', e.evt)
      //   return
      // }

      // this.emit('click', e.evt, Number(e.target.getAttr('_id')))

      // this._focus([e.target] as Konva.Rect[])
    })

    this._tr.on('transformstart', () => {
      this.snapping.snappingEdges = [
        'vertical-start',
        'vertical-end',
        'horizontal-start',
        'horizontal-end',
      ]
    })

    this._tr.on('transformend', () => {
      this._snapRect = null

      this.snapping.snappingEdges = [
        'vertical-start',
        'vertical-center',
        'vertical-end',
        'horizontal-start',
        'horizontal-center',
        'horizontal-end',
      ]
    })
  }

  addGroupNode(track: Track, zIndex: number): Konva.Group {
    const node = new Konva.Group({
      id: track.id,
      _zIndex: zIndex,
    })

    this._layer.add(node)

    return node
  }

  /**
   * 增加幽灵节点，用于被选中
   */
  addGhostNode(
    segment: SegmentText | SegmentSticker | SegmentVideo,
    options: { visible: boolean; zIndex: number },
  ): Konva.Rect {
    const { x, y, id, scale_x, scale_y, width, height } = segment

    const isGroupChild = isSegmentGroupChild(segment)

    const node = new Konva.Rect({
      x,
      y: -y,
      width,
      height,
      name: `${GHOST} ${SNAP_NODE}`,
      draggable: false,
      scaleX: scale_x / 100,
      scaleY: scale_y / 100,
      id: getUniqueId(segment),
      _id: id,
      offsetX: width / 2,
      offsetY: height / 2,
      visible: options.visible,
      // fill: 'red',
      rotation: segment.rotation,
      isGroup: segment.isGroup,
      // opacity: 0.7,
      isGroupChild,
      stroke: 'rgb(228, 231, 237)',
      strokeWidth: 1,
      dash: [4, 4],
      strokeEnabled: false,
      strokeScaleEnabled: false,
    })

    const group = this._getNode(
      isGroupChild ? segment.pid : segment.track_id,
    ) as Konva.Group
    group?.add(node)

    return node
  }

  private _focus(nodes: Konva.Rect[]) {
    this._tr.nodes().forEach((item) => {
      item.setDraggable(false)

      item.setAttrs({
        fill: null,
      })
    })
    this._tr.nodes(nodes)
    nodes.forEach((node) => {
      this.updateTransformerStyle(
        node as Konva.Rect,
        nodes.length > 1 ? false : node.getAttr('isGroup'),
      )

      node.setDraggable(true)

      node.parent?.moveToTop()
      this._tr.moveToTop()

      initAnchorsStyle(this._tr)

      this._updateBoundBoxFunc(node as Konva.Rect)
    })

    this._tr.forceUpdate()
  }

  focus(segments: SegmentCollection[]): void {
    const nodes = segments
      .map((segment) => this._getNode(getUniqueId(segment)))
      .filter((t) => typeof t !== 'undefined') as Konva.Rect[]

    // if (typeof node !== 'undefined') {
    //   this._focus([node] as Konva.Rect[])
    // }
    if (nodes.length > 1) {
      this._updateNodesCover(nodes as Konva.Rect[])

      this._nodesCover.moveToTop()
      // this._nodesCover.moveToBottom()

      this._focus([...nodes, this._nodesCover])
    } else {
      this._focus(nodes)
    }
  }

  unfocus(): void {
    this._tr.nodes().forEach((item) => {
      item.setDraggable(false)

      item.setAttrs({
        fill: null,
      })

      item.parent?.zIndex(item.parent.getAttr('_zIndex'))
    })

    this._tr.nodes([])

    this._updateNodesCover([])
  }

  private _updateNodesCover(nodes: Konva.Rect[]): void {
    const rect = getRectFromNodes(nodes, {
      relativeTo: this._layer,
    })

    this._nodesCover.setAttrs({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      rotation: 0,
      name: 'nodesCover',
    })
  }

  getNode(
    id: ProjectSegmentModel['id'] | Track['id'],
  ): Konva.Rect | Konva.Group | undefined {
    return this._getNode(id)
  }

  /** 切换元件的显隐状态 */
  toggleNode(segment: SegmentCollection, value: boolean): void {
    const node = this._getNode(getUniqueId(segment))

    if (typeof node === 'undefined') return
    node.setAttr('visible', value)

    if (this._tr?.nodes().includes(node)) {
      this._tr.setAttr('visible', value)
    }
  }

  private _emitTransform(node: Konva.Rect): void {
    this.emit('transform', transformNodeData(node))
  }

  private _bindTransformerEvents(): void {
    this._tr.on('dragstart transformstart', (e) => {
      this.emit('transformStart', e)
    })

    this._tr.on('dragend transformend', () => {
      this.emit('transformEnd')
    })
  }

  private _getNode(
    id: ProjectSegmentModel['id'] | Track['id'],
  ): Konva.Rect | Konva.Group | undefined {
    return this._layer.findOne(`#${id}`) as Konva.Rect | Konva.Group
  }

  update(segment: SegmentText | SegmentSticker | SegmentVideo): void {
    const node = this._getNode(getUniqueId(segment))

    if (typeof node === 'undefined') return

    node.setAttrs({
      x: segment.x,
      y: -segment.y,
      rotation: segment.rotation,
      width: segment.width,
      height: segment.height,
      offsetX: segment.width / 2,
      offsetY: segment.height / 2,
      scaleX: segment.scale_x / 100,
      scaleY: segment.scale_y / 100,
    })

    // if (!node.getAttr('isGroupChild')) {
    //   node.setAttrs({
    //     x: segment.x,
    //     y: -segment.y,
    //     offsetX: segment.width / 2,
    //     offsetY: segment.height / 2,
    //     rotation: segment.rotation,
    //   })
    // }
  }

  removeNode(id: string): void {
    const node = this._getNode(id)

    if (typeof node === 'undefined') return
    console.log('segment transformer remove', node)
    node.destroy()
  }

  removeGroup(track: Track): void {
    const node = this._getNode(track.id)

    if (typeof node === 'undefined') return

    node.destroy()
  }

  stage(): Konva.Stage {
    return this._stage
  }

  layer(): Konva.Layer {
    return this._layer
  }

  keepRatio(value: boolean): void {
    this._tr.keepRatio(value)
  }

  /**
   * 更新选中样式
   * @param node Konva.Rect
   * @param isGroup boolean
   */
  private updateTransformerStyle(node: Konva.Rect, isGroup: boolean) {
    node.setAttrs({
      fill: isGroup ? primaryFill : null,
    })
    this._tr.setAttrs(
      isGroup ? primaryTransformerStyle : normalTransformerStyle,
    )
    this._tr.setAttr('rotateEnabled', !node.getAttr('isGroupChild'))
  }

  addGroupZone(
    segment: SegmentText | SegmentSticker | SegmentVideo,
  ): Konva.Group {
    const { x, y, id, scale_x, scale_y } = segment

    const width = segment.width * (scale_x / 100)
    const height = segment.height * (scale_y / 100)

    const group = new Konva.Group({
      id: String(id),
      rotation: segment.rotation,
      x,
      y: -y,
      width,
      height,
      offsetX: width / 2,
      offsetY: height / 2,
    })

    const node = new Konva.Rect({
      x: 0,
      y: 0,
      width,
      height,
      draggable: false,
      strokeWidth: 4,
      stroke: primaryTransformerStyle.borderStroke,
      name: SNAP_NODE,
    })

    group.add(node)

    node.setAttr('zIndex', 1)

    this._layer.add(group)

    return group
  }

  updateGroupZone(segment: SegmentText | SegmentSticker | SegmentVideo): void {
    const { x, y, id, scale_x, scale_y } = segment

    const width = segment.width * (scale_x / 100)
    const height = segment.height * (scale_y / 100)

    const group = this._getNode(String(id)) as Konva.Group

    if (group) {
      const node = group?.findOne(`.${SNAP_NODE}`) as Konva.Rect

      group.setAttrs({
        rotation: segment.rotation,
        x,
        y: -y,
        width,
        height,
        offsetX: width / 2,
        offsetY: height / 2,
      })

      if (node) {
        node.setAttrs({
          width,
          height,
        })
      }
    }
  }

  private _addGhostCanvas(): Konva.Rect {
    const node = new Konva.Rect({
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      offsetX: 0,
      offsetY: 0,
      draggable: false,
      name: SNAP_NODE,
      // fill: 'green',
      // opacity: 0.7,
    })

    // 让这个元件不会被鼠标事件触发
    node.hitFunc((context) => {
      context.beginPath()
      context.rect(0, 0, 0, 0)
      context.closePath()
      // important Konva method that fill and stroke shape from its properties
      context.fillStrokeShape(node)
    })

    // this._layer.add(node)

    return node
  }

  get tr(): Konva.Transformer {
    return this._tr
  }
}

export const transformer = new Transformer()
